import React, { useState } from "react";
import { X } from "lucide-react";
import { generateRecaptchaToken } from "../lib/recaptcha";

interface BookDemoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function BookDemoModal({ isOpen, onClose }: BookDemoModalProps) {
  const [form, setForm] = useState({ name: "", email: "", firm: "" });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.firm) {
      setError("All fields are required.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      let recaptchaToken = "";
      try { recaptchaToken = await generateRecaptchaToken("book_demo"); } catch {}
      const res = await fetch("/api/templasure/book-demo/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: form.name, email: form.email, firm: form.firm, source: "modal", recaptchaToken }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || "Submission failed");
      }
      setSuccess(true);
      setTimeout(() => { setSuccess(false); onClose(); setForm({ name: "", email: "", firm: "" }); }, 2000);
    } catch (err: any) {
      setError(err.message || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "w-full rounded-xl border border-[#1A4035] bg-[#0D2B22] text-white placeholder:text-[#9ABFB5] px-4 py-3 focus:outline-none focus:border-[#2EB39A] focus:ring-1 focus:ring-[#2EB39A] transition-colors";

  return (
    <div data-impeccable-variants="dc730bed" data-impeccable-variant-count="3" style={{ display: "contents" }}>
      {/* impeccable-variants-start dc730bed */}
      {/* Original */}
      <div data-impeccable-variant="original">
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0,0,0,0.7)" }}
          onClick={onClose}
        >
          <div
            className="relative w-full max-w-lg rounded-2xl border border-[#1A4035] bg-[#0F3328] p-8 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={onClose}
              className="absolute top-4 right-4 text-[#9ABFB5] hover:text-white transition-colors"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="mb-6">
              <div className="mb-3">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-widest text-[#2EB39A]" style={{ backgroundColor: "rgba(46,179,154,0.12)", border: "1px solid rgba(46,179,154,0.3)" }}>
                  Book a Demo
                </span>
              </div>
              <h2 className="text-2xl font-bold text-white">
                See Revisr in your practice context.
              </h2>
              <p className="mt-2 text-sm text-[#9ABFB5]">
                A 30-minute call. No commitments. We'll show you how governed multi-client delivery works for your engagements.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="mb-1 block text-sm text-[#9ABFB5]">Full name</label>
                <input
                  type="text"
                  placeholder="Jordan Okafor"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm text-[#9ABFB5]">Work email</label>
                <input
                  type="email"
                  placeholder="you@practice.com.au"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm text-[#9ABFB5]">Practice / firm</label>
                <input
                  type="text"
                  placeholder="How many clients do you manage?"
                  value={form.firm}
                  onChange={(e) => setForm({ ...form, firm: e.target.value })}
                  className={inputClass}
                />
              </div>

              {error && <p className="text-sm text-red-400">{error}</p>}
              {success && <p className="text-sm text-[#2EB39A]">✓ We'll be in touch soon.</p>}

              <button
                type="submit"
                disabled={submitting || success}
                className="mt-2 w-full rounded-xl bg-[#2EB39A] py-3 font-bold text-[#0D2B22] transition-colors hover:bg-[#25927E] disabled:opacity-50"
              >
                {submitting ? "Sending…" : success ? "Sent!" : "Request my demo"}
              </button>
            </form>
          </div>
        </div>
      </div>
      {/* Variants: insert below this line */}
      {/* impeccable-variants-end dc730bed */}
    </div>
  );
}
